// frontend/src/components/RecepcionarCompraModal.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import '../styles/modals.css';
import './CrearModal.css';

const RecepcionarCompraModal = ({ show, onClose, ordenCompra, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [bodegas, setBodegas] = useState([]);
  const [recursos, setRecursos] = useState([]);

  // Estados del formulario
  const [formData, setFormData] = useState({
    numero_guia: '',
    fecha_recepcion: new Date().toISOString().split('T')[0],
    fecha_emision_guia: '',
    bodega: '',
    observaciones: '',
    estado: 'recepcionado' // Por defecto recepcionado para crear el movimiento automáticamente
  });

  // Estados de los detalles (cantidad recibida para cada item)
  const [detallesRecepcion, setDetallesRecepcion] = useState([]);

  useEffect(() => {
    if (show && ordenCompra) {
      const cargarDatos = async () => {
        await cargarBodegas();
        cargarRecursos();
        inicializarDetalles();

        // Preseleccionar la bodega si viene de una solicitud (después de cargar las bodegas)
        if (ordenCompra.bodega_solicitud_id) {
          setFormData(prev => ({
            ...prev,
            bodega: ordenCompra.bodega_solicitud_id.toString()
          }));
          console.log('✓ Bodega preseleccionada:', ordenCompra.bodega_solicitud_nombre);
        }
      };

      cargarDatos();
    }
  }, [show, ordenCompra]);

  const cargarBodegas = async () => {
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/bodega');
      const response = await api.get(`${API_BASE_URL}/bodegas/`);
      const bodegasActivas = (response.data.results || response.data).filter(
        b => b.status === 'activo'
      );
      setBodegas(bodegasActivas);
    } catch (error) {
      console.error('Error al cargar bodegas:', error);
      toast.error('Error al cargar las bodegas');
    }
  };

  const cargarRecursos = async () => {
    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/onebase');
      const response = await api.get(`${API_BASE_URL}/recursos/`);
      setRecursos(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar recursos:', error);
      toast.error('Error al cargar los recursos');
    }
  };

  const inicializarDetalles = () => {
    if (ordenCompra && ordenCompra.detalles) {
      const detalles = ordenCompra.detalles.map(detalle => {
        // Intentar obtener el recurso_id desde múltiples fuentes (en orden de prioridad):
        // 1. recurso_data.recurso_id (nuevo campo que funciona para ambos casos)
        // 2. recurso_data.recurso_referencia_id (campo original)
        // 3. detalle.recurso (campo directo nuevo del backend)
        const recursoId = detalle.recurso_data?.recurso_id
          || detalle.recurso_data?.recurso_referencia_id
          || detalle.recurso;

        // Convertir a número entero para que coincida con el value del select
        const recursoIdNumero = recursoId ? parseInt(recursoId) : null;

        const recursoNombre = detalle.recurso_data?.nombre
          || detalle.recurso_nombre
          || detalle.descripcion;

        console.log('Detalle OC:', detalle);
        console.log('Recurso ID extraído:', recursoId);
        console.log('Recurso ID convertido a número:', recursoIdNumero);

        // Si no tiene recurso asociado, permitir selección manual
        const necesitaSeleccionManual = !recursoIdNumero;

        if (necesitaSeleccionManual) {
          toast.warning(`El item "${recursoNombre}" no tiene recurso asociado. Debes seleccionarlo manualmente.`);
        }

        return {
          detalle_orden_compra: detalle.id,
          recurso: recursoIdNumero,
          recurso_nombre: recursoNombre,
          cantidad_ordenada: parseFloat(detalle.cantidad),
          cantidad_recibida: parseFloat(detalle.cantidad), // Por defecto, toda la cantidad
          precio_unitario: parseFloat(detalle.precio_unitario),
          unidad: detalle.unidad || 'unidad',
          observaciones: '',
          presupuesto_costo_recurso: detalle.presupuesto_costo_recurso,
          necesita_seleccion_manual: necesitaSeleccionManual
        };
      });
      setDetallesRecepcion(detalles);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCantidadRecibidaChange = (index, value) => {
    const newDetalles = [...detallesRecepcion];
    const cantidadRecibida = parseFloat(value) || 0;
    const cantidadOrdenada = newDetalles[index].cantidad_ordenada;

    // Validar que no sea mayor a la ordenada
    if (cantidadRecibida > cantidadOrdenada) {
      toast.warning('La cantidad recibida no puede ser mayor a la cantidad ordenada');
      return;
    }

    newDetalles[index].cantidad_recibida = cantidadRecibida;
    setDetallesRecepcion(newDetalles);
  };

  const handleObservacionesDetalleChange = (index, value) => {
    const newDetalles = [...detallesRecepcion];
    newDetalles[index].observaciones = value;
    setDetallesRecepcion(newDetalles);
  };

  const handleRecursoChange = (index, recursoId) => {
    const newDetalles = [...detallesRecepcion];
    newDetalles[index].recurso = parseInt(recursoId);
    newDetalles[index].necesita_seleccion_manual = false;
    setDetallesRecepcion(newDetalles);
  };

  const validarFormulario = () => {
    if (!formData.numero_guia.trim()) {
      toast.error('Debe ingresar el número de guía');
      return false;
    }

    if (!formData.fecha_recepcion) {
      toast.error('Debe ingresar la fecha de recepción');
      return false;
    }

    if (!formData.bodega) {
      toast.error('Debe seleccionar una bodega');
      return false;
    }

    // Validar que al menos un detalle tenga cantidad recibida
    const tieneRecepcion = detallesRecepcion.some(d => d.cantidad_recibida > 0);
    if (!tieneRecepcion) {
      toast.error('Debe recepcionar al menos un item');
      return false;
    }

    // Validar que todos los detalles tengan recurso_id
    const detallesSinRecurso = detallesRecepcion.filter(d => d.cantidad_recibida > 0 && !d.recurso);
    if (detallesSinRecurso.length > 0) {
      console.error('Detalles sin recurso:', detallesSinRecurso);
      toast.error('Debes seleccionar un recurso para todos los items marcados con ⚠️');
      return false;
    }

    // Validar que no haya items con selección manual pendiente
    const itemsPendientes = detallesRecepcion.filter(d => d.cantidad_recibida > 0 && d.necesita_seleccion_manual);
    if (itemsPendientes.length > 0) {
      toast.error('Debes completar la selección de recursos para todos los items marcados');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setLoading(true);

    try {
      const baseURL = api?.defaults?.baseURL || '/api/content';
      const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/bodega');

      // Filtrar solo los detalles con cantidad recibida > 0
      const detallesData = detallesRecepcion
        .filter(d => d.cantidad_recibida > 0)
        .map(detalle => ({
          detalle_orden_compra: detalle.detalle_orden_compra,
          recurso: detalle.recurso,
          cantidad_ordenada: detalle.cantidad_ordenada,
          cantidad_recibida: detalle.cantidad_recibida,
          precio_unitario: detalle.precio_unitario,
          unidad: detalle.unidad,
          observaciones: detalle.observaciones,
          presupuesto_costo_recurso: detalle.presupuesto_costo_recurso
        }));

      console.log('Detalles a enviar:', detallesData);

      const payload = {
        orden_compra: ordenCompra.id,
        numero_guia: formData.numero_guia,
        fecha_recepcion: formData.fecha_recepcion,
        fecha_emision_guia: formData.fecha_emision_guia || null,
        bodega: parseInt(formData.bodega),
        observaciones: formData.observaciones,
        estado: formData.estado,
        detalles_data: detallesData
      };

      console.log('Payload completo:', payload);

      await api.post(`${API_BASE_URL}/guias-despacho/`, payload);

      toast.success('Guía de despacho creada correctamente');

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Error al crear guía de despacho:', error);
      const errorMsg = error.response?.data?.detalles_data?.[0]
        || error.response?.data?.detail
        || 'Error al crear la guía de despacho';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(value);
  };

  const calcularTotalRecibido = () => {
    return detallesRecepcion.reduce((sum, detalle) => {
      return sum + (detalle.cantidad_recibida * detalle.precio_unitario);
    }, 0);
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content modal-xl"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '1100px' }}
      >
        <div className="modal-header">
          <h2>📦 Recepcionar Compra - OC {ordenCompra?.numero_oc}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Información de la Orden de Compra */}
          <div className="form-section">
            <h3>Información de la Orden de Compra</h3>
            <div className="info-grid">
              <div className="info-item">
                <strong>Proveedor:</strong> {ordenCompra?.proveedor_nombre || '-'}
              </div>
              <div className="info-item">
                <strong>Proyecto:</strong> {ordenCompra?.proyecto_nombre || '-'}
              </div>
              <div className="info-item">
                <strong>Fecha Emisión:</strong> {ordenCompra?.fecha_emision
                  ? new Date(ordenCompra.fecha_emision).toLocaleDateString('es-CL')
                  : '-'}
              </div>
              <div className="info-item">
                <strong>Total OC:</strong> {formatCurrency(ordenCompra?.total_oc || 0)}
              </div>
            </div>
          </div>

          {/* Datos de la Guía de Despacho */}
          <div className="form-section">
            <h3>Datos de la Guía de Despacho</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="numero_guia">
                  Número de Guía <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="numero_guia"
                  name="numero_guia"
                  value={formData.numero_guia}
                  onChange={handleInputChange}
                  placeholder="Ej: GD-2025-001"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="bodega">
                  Bodega de Recepción <span className="required">*</span>
                </label>
                <select
                  id="bodega"
                  name="bodega"
                  value={formData.bodega}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccione una bodega</option>
                  {bodegas.map(bodega => (
                    <option key={bodega.id} value={bodega.id}>
                      {bodega.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fecha_recepcion">
                  Fecha de Recepción <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="fecha_recepcion"
                  name="fecha_recepcion"
                  value={formData.fecha_recepcion}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="fecha_emision_guia">
                  Fecha Emisión Guía
                </label>
                <input
                  type="date"
                  id="fecha_emision_guia"
                  name="fecha_emision_guia"
                  value={formData.fecha_emision_guia}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="observaciones">Observaciones</label>
              <textarea
                id="observaciones"
                name="observaciones"
                value={formData.observaciones}
                onChange={handleInputChange}
                rows="3"
                placeholder="Observaciones generales de la recepción..."
              />
            </div>
          </div>

          {/* Detalles de Recepción */}
          <div className="form-section">
            <h3>Items a Recepcionar</h3>
            <div className="table-container">
              <table className="detalles-table">
                <thead>
                  <tr>
                    <th>Recurso</th>
                    <th style={{ textAlign: 'center', width: '120px' }}>Cant. Ordenada</th>
                    <th style={{ textAlign: 'center', width: '120px' }}>Cant. Recibida</th>
                    <th style={{ textAlign: 'right', width: '120px' }}>Precio Unit.</th>
                    <th style={{ textAlign: 'right', width: '120px' }}>Subtotal</th>
                    <th style={{ width: '250px' }}>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {detallesRecepcion.map((detalle, index) => (
                    <tr key={index} style={{ backgroundColor: detalle.necesita_seleccion_manual ? '#fef3c7' : 'transparent' }}>
                      <td>
                        <div>
                          {detalle.necesita_seleccion_manual && (
                            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#f59e0b' }}>
                              ⚠️ Seleccione el recurso
                            </div>
                          )}
                          <select
                            value={detalle.recurso || ''}
                            onChange={(e) => handleRecursoChange(index, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: detalle.necesita_seleccion_manual ? '2px solid #f59e0b' : '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                              backgroundColor: detalle.recurso ? '#f9fafb' : 'white'
                            }}
                            required
                          >
                            <option value="">Seleccione un recurso...</option>
                            {recursos.map(rec => (
                              <option key={rec.id} value={rec.id}>
                                {rec.nombre} - {rec.unidad}
                              </option>
                            ))}
                          </select>
                          {detalle.recurso && (
                            <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
                              {detalle.unidad}
                            </small>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {detalle.cantidad_ordenada.toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="number"
                          value={detalle.cantidad_recibida}
                          onChange={(e) => handleCantidadRecibidaChange(index, e.target.value)}
                          min="0"
                          max={detalle.cantidad_ordenada}
                          step="0.01"
                          style={{
                            width: '90px',
                            textAlign: 'center',
                            padding: '6px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px'
                          }}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(detalle.precio_unitario)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>
                        {formatCurrency(detalle.cantidad_recibida * detalle.precio_unitario)}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={detalle.observaciones}
                          onChange={(e) => handleObservacionesDetalleChange(index, e.target.value)}
                          placeholder="Observaciones..."
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.875rem'
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', fontWeight: 700 }}>
                    <td colSpan="4" style={{ textAlign: 'right', padding: '16px', fontSize: '1.1rem' }}>
                      Total Recibido:
                    </td>
                    <td style={{ textAlign: 'right', padding: '16px', fontSize: '1.1rem', color: '#69B32D' }}>
                      {formatCurrency(calcularTotalRecibido())}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Procesando...' : '✓ Recepcionar Compra'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecepcionarCompraModal;
